import React, { Children, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import c from "classnames/bind";

import { isItem, Item } from "./grid.item";
import styles from "./grid.module.scss";

const cx = c.bind(styles);

export type GridProps = Omit<BaseProps, "children"> & {
  children: ReactNode;
  containerClassName?: string;
  /** The number of columns on small screens. */
  colsSm?: 1 | 2 | 3 | 4;
  /** The number of columns on medium screens. */
  colsMd?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** The number of columns on large screens. */
  colsLg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  /** Eliminates gaps between columns and rows. */
  noGap?: boolean;
  /** Eliminates gaps between columns. */
  noColGap?: boolean;
  /** Eliminates gaps between rows. */
  noRowGap?: boolean;
};

export type GridCmp<P> = React.FunctionComponent<P> & {
  Item: typeof Item;
};

const rootClassName = "purpur-grid";
const innerClassName = "purpur-grid__inner";

export const Grid = ({
  children,
  className,
  containerClassName,
  colsSm = 1,
  colsMd = 1,
  colsLg = 1,
  noGap = false,
  noColGap = false,
  noRowGap = false,
  ...props
}: GridProps) => {
  const gridItemChildren = Children.toArray(children).filter(isItem);
  const hasItemChildren = !!gridItemChildren.length;

  const containerClasses = cx([rootClassName, containerClassName]);

  const classes = cx([
    innerClassName,
    {
      [`${innerClassName}--grid`]: !hasItemChildren,
      [`${innerClassName}--flex`]: hasItemChildren,
      [`${innerClassName}--no-gap`]: noGap,
      [`${innerClassName}--no-col-gap`]: noColGap,
      [`${innerClassName}--no-row-gap`]: noRowGap,
      [`${innerClassName}-col-sm-${colsSm}`]: colsSm > 1 && !hasItemChildren,
      [`${innerClassName}-col-md-${colsMd}`]: colsMd > 1 && !hasItemChildren,
      [`${innerClassName}-col-lg-${colsLg}`]: colsLg > 1 && !hasItemChildren,
    },
    className,
  ]);

  return (
    <div className={containerClasses} {...props}>
      <div className={classes}>
        {hasItemChildren
          ? Children.map(gridItemChildren, ({ props: { children, ...colProps } }) => (
              <Item {...colProps} noColGap={noGap || noColGap}>
                {children}
              </Item>
            ))
          : children}
      </div>
    </div>
  );
};

Grid.displayName = "Grid";

Grid.Item = Item;
